import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatDuration, formatWhen } from '../format.ts';
import type { Sop, SopMarkupAnnotation, SopSection } from '../../../request/sop-synthesizer/sopSynthesizer.api.ts';
import { parseSopMarkdown } from './parseSopMarkdown.ts';

const NAVY: [number, number, number] = [30, 64, 175];
const HL_FILL: [number, number, number] = [250, 204, 21];
const COMMENT_FILL: [number, number, number] = [239, 246, 255];
const COMMENT_BORDER: [number, number, number] = [186, 230, 253];
const MARGIN = 14;

export type SopPdfInput = {
  sop: Sop;
  sections: SopSection[];
  frames: Record<string, string>;
  videoTitle?: string;
  videoDurationSeconds?: number;
  markupAnnotations?: SopMarkupAnnotation[];
};

function safeFilename(title: string): string {
  const base = title.trim().replace(/[^\w\s-]+/g, '').replace(/\s+/g, '-').slice(0, 80);
  return `${base || 'sop'}.pdf`;
}

type LoadedFrame = { dataUrl: string; width: number; height: number };

/** Decode frame URL via canvas → JPEG so jsPDF does not garble PNG/WebP bytes. */
async function loadFrameForPdf(url: string): Promise<LoadedFrame | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error('image load failed'));
        el.src = objectUrl;
      });
      const width = img.naturalWidth || img.width;
      const height = img.naturalHeight || img.height;
      if (!width || !height) return null;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0);
      return { dataUrl: canvas.toDataURL('image/jpeg', 0.92), width, height };
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } catch {
    return null;
  }
}

function ensureSpace(pdf: jsPDF, y: number, need: number): number {
  const pageHeight = pdf.internal.pageSize.getHeight();
  if (y + need <= pageHeight - MARGIN) return y;
  pdf.addPage();
  return MARGIN;
}

function writeWrapped(pdf: jsPDF, text: string, x: number, y: number, maxWidth: number, lineHeight: number): number {
  const lines = pdf.splitTextToSize(text, maxWidth) as string[];
  for (const line of lines) {
    y = ensureSpace(pdf, y, lineHeight);
    pdf.text(line, x, y);
    y += lineHeight;
  }
  return y;
}

function splitHighlightSegments(text: string, quotes: string[]): { text: string; highlight: boolean }[] {
  const unique = [...new Set(quotes.map((q) => q.trim()).filter((q) => q.length >= 2))].sort(
    (a, b) => b.length - a.length,
  );
  if (!unique.length || !text) return [{ text, highlight: false }];

  type Hit = { start: number; end: number };
  const hits: Hit[] = [];
  const lower = text.toLowerCase();
  for (const q of unique) {
    const needle = q.toLowerCase();
    let from = 0;
    while (from < text.length) {
      const idx = lower.indexOf(needle, from);
      if (idx === -1) break;
      const end = idx + q.length;
      const overlaps = hits.some((h) => idx < h.end && end > h.start);
      if (!overlaps) hits.push({ start: idx, end });
      from = idx + 1;
    }
  }
  hits.sort((a, b) => a.start - b.start);
  if (!hits.length) return [{ text, highlight: false }];

  const out: { text: string; highlight: boolean }[] = [];
  let cursor = 0;
  for (const h of hits) {
    if (h.start > cursor) out.push({ text: text.slice(cursor, h.start), highlight: false });
    out.push({ text: text.slice(h.start, h.end), highlight: true });
    cursor = h.end;
  }
  if (cursor < text.length) out.push({ text: text.slice(cursor), highlight: false });
  return out;
}

function writeWrappedHighlighted(
  pdf: jsPDF,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
  highlightQuotes: string[],
): number {
  const segments = splitHighlightSegments(text, highlightQuotes);
  let cx = x;
  let cy = y;
  const maxX = x + maxWidth;

  const drawToken = (token: string, highlight: boolean) => {
    if (!token) return;
    const parts = token.match(/\s+|\S+/g) ?? [token];
    for (const part of parts) {
      const w = pdf.getTextWidth(part);
      if (cx > x && cx + w > maxX) {
        cy = ensureSpace(pdf, cy + lineHeight, lineHeight);
        cx = x;
      }
      if (cx === x && /^\s+$/.test(part)) continue;
      if (highlight && !/^\s+$/.test(part)) {
        pdf.setFillColor(...HL_FILL);
        pdf.rect(cx - 0.2, cy - lineHeight + 1.4, w + 0.4, lineHeight - 0.6, 'F');
      }
      pdf.setTextColor(31, 41, 55);
      pdf.text(part, cx, cy);
      cx += w;
    }
  };

  for (const seg of segments) drawToken(seg.text, seg.highlight);
  return cy + lineHeight;
}

function textContainsQuote(haystack: string, quote: string): boolean {
  return haystack.toLowerCase().includes(quote.trim().toLowerCase());
}

function pdfHexColor(color: string | undefined): [number, number, number] {
  const raw = (color || '#ef4444').trim();
  const m = /^#?([0-9a-f]{6})$/i.exec(raw);
  if (!m) return [239, 68, 68];
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function drawImageMarksOnPdf(
  pdf: jsPDF,
  marks: SopMarkupAnnotation[],
  ox: number,
  oy: number,
  w: number,
  h: number,
): void {
  for (const a of marks) {
    const [r, g, b] = pdfHexColor(a.color);
    pdf.setDrawColor(r, g, b);
    pdf.setTextColor(r, g, b);
    pdf.setLineWidth(0.6);
    if ((a.type === 'image_rect' || a.type === 'image_ellipse') && a.x != null && a.y != null && a.w != null && a.h != null) {
      const x = ox + a.x * w;
      const y = oy + a.y * h;
      const rw = a.w * w;
      const rh = a.h * h;
      if (a.type === 'image_rect') pdf.rect(x, y, rw, rh);
      else pdf.ellipse(x + rw / 2, y + rh / 2, rw / 2, rh / 2);
    } else if (a.type === 'image_arrow' && a.x1 != null && a.y1 != null && a.x2 != null && a.y2 != null) {
      const x1 = ox + a.x1 * w;
      const y1 = oy + a.y1 * h;
      const x2 = ox + a.x2 * w;
      const y2 = oy + a.y2 * h;
      pdf.line(x1, y1, x2, y2);
      const ang = Math.atan2(y2 - y1, x2 - x1);
      const ah = 3.2;
      pdf.line(x2, y2, x2 - ah * Math.cos(ang - 0.4), y2 - ah * Math.sin(ang - 0.4));
      pdf.line(x2, y2, x2 - ah * Math.cos(ang + 0.4), y2 - ah * Math.sin(ang + 0.4));
    } else if (a.type === 'image_pen' && Array.isArray(a.points) && a.points.length > 1) {
      for (let i = 1; i < a.points.length; i += 1) {
        const p0 = a.points[i - 1];
        const p1 = a.points[i];
        if (!p0 || !p1 || p0.length < 2 || p1.length < 2) continue;
        pdf.line(ox + p0[0] * w, oy + p0[1] * h, ox + p1[0] * w, oy + p1[1] * h);
      }
    } else if (a.type === 'image_text' && a.x != null && a.y != null && a.text) {
      pdf.setFontSize(8);
      pdf.text(a.text.slice(0, 60), ox + a.x * w, oy + a.y * h + 3);
    } else if (a.type === 'image_callout' && a.x != null && a.y != null) {
      const cx = ox + a.x * w;
      const cy = oy + a.y * h;
      pdf.setFillColor(r, g, b);
      pdf.circle(cx, cy, 3, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(7);
      pdf.text(String(a.callout_number ?? a.text ?? ''), cx, cy + 1, { align: 'center' });
      pdf.setTextColor(r, g, b);
    } else if (a.type === 'image_pin' && a.x != null && a.y != null) {
      pdf.setFillColor(r, g, b);
      pdf.circle(ox + a.x * w, oy + a.y * h, 2.2, 'F');
    }
  }
  pdf.setTextColor(31, 41, 55);
  pdf.setDrawColor(0);
}

function writeSectionCommentsList(
  pdf: jsPDF,
  y: number,
  contentWidth: number,
  comments: SopMarkupAnnotation[],
): number {
  if (!comments.length) return y;
  y += 2;
  y = ensureSpace(pdf, y, 10);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(8);
  pdf.setTextColor(30, 64, 175);
  pdf.text('COMMENTS', MARGIN, y);
  y += 5;

  for (const c of comments) {
    const quote = (c.quote || '').trim();
    const note = (c.comment || '').trim();
    const quoteLines = pdf.splitTextToSize(`"${quote}"`, contentWidth - 6) as string[];
    const noteLines = note ? (pdf.splitTextToSize(note, contentWidth - 6) as string[]) : [];
    const boxH = Math.max(10, 3 + quoteLines.length * 3.8 + (noteLines.length ? noteLines.length * 3.8 + 1 : 0) + 3);
    y = ensureSpace(pdf, y, boxH + 2);
    pdf.setFillColor(...COMMENT_FILL);
    pdf.setDrawColor(...COMMENT_BORDER);
    pdf.roundedRect(MARGIN, y, contentWidth, boxH, 1, 1, 'FD');
    pdf.setFont('helvetica', 'italic');
    pdf.setFontSize(8);
    pdf.setTextColor(55, 65, 81);
    let ty = y + 4;
    for (const line of quoteLines) {
      pdf.text(line, MARGIN + 2, ty);
      ty += 3.8;
    }
    if (noteLines.length) {
      ty += 0.5;
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(17, 24, 39);
      for (const line of noteLines) {
        pdf.text(line, MARGIN + 2, ty);
        ty += 3.8;
      }
    }
    y += boxH + 2;
  }

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(31, 41, 55);
  return y;
}

/** Build and download an SOP PDF with jsPDF (text + tables + screenshots) — no HTML screenshot. */
export async function downloadSopPdf(input: SopPdfInput): Promise<void> {
  const { sop, sections, frames, videoTitle, videoDurationSeconds, markupAnnotations = [] } = input;
  if (!sections.length) {
    throw new Error('This SOP has no sections to export yet.');
  }

  const frameIds = new Set<string>();
  for (const section of sections) {
    for (const block of parseSopMarkdown(section.body)) {
      if (block.type === 'image') frameIds.add(block.frameId);
    }
  }
  const imageData = new Map<string, LoadedFrame>();
  await Promise.all(
    [...frameIds].map(async (id) => {
      const url = frames[id];
      if (!url) return;
      const loaded = await loadFrameForPdf(url);
      if (loaded) imageData.set(id, loaded);
    }),
  );

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const contentWidth = pageWidth - MARGIN * 2;
  let y = MARGIN;

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(16);
  pdf.setTextColor(17, 24, 39);
  y = writeWrapped(pdf, sop.title, MARGIN, y + 4, contentWidth, 7);
  y += 2;

  if (sop.description?.trim()) {
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(75, 85, 99);
    y = writeWrapped(pdf, sop.description.trim(), MARGIN, y, contentWidth, 5);
    y += 2;
  }

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(107, 114, 128);
  const meta = [
    `Version v${sop.row_version}`,
    sop.updated_at ? `Updated ${formatWhen(sop.updated_at)}` : null,
    videoTitle
      ? `Source: ${videoTitle}${videoDurationSeconds ? ` (${formatDuration(videoDurationSeconds)})` : ''}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');
  y = writeWrapped(pdf, meta, MARGIN, y, contentWidth, 4.5);
  y += 6;

  for (let s = 0; s < sections.length; s += 1) {
    const section = sections[s];
    const sectionMarks = markupAnnotations.filter((a) => a.section_id === section.id);
    const highlightQuotes = sectionMarks
      .filter((a) => a.type === 'highlight' && a.quote)
      .map((a) => a.quote as string);
    const sectionComments = sectionMarks.filter((a) => a.type === 'comment');
    const sectionImageMarks = sectionMarks.filter((a) => typeof a.type === 'string' && a.type.startsWith('image_'));

    y = ensureSpace(pdf, y, 12);
    pdf.setFillColor(...NAVY);
    pdf.rect(MARGIN, y, contentWidth, 8, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(255, 255, 255);
    const heading = `${s + 1}. ${section.heading}`.toUpperCase();
    pdf.text(pdf.splitTextToSize(heading, contentWidth - 4)[0], MARGIN + 2, y + 5.5);
    y += 12;

    pdf.setTextColor(31, 41, 55);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);

    for (const block of parseSopMarkdown(section.body)) {
      if (block.type === 'heading') {
        y = ensureSpace(pdf, y, 12);
        const boxTop = y;
        const boxH = 8;
        pdf.setFillColor(243, 244, 246);
        pdf.rect(MARGIN, boxTop, contentWidth, boxH, 'F');
        pdf.setDrawColor(...NAVY);
        pdf.setLineWidth(0.9);
        pdf.line(MARGIN, boxTop, MARGIN, boxTop + boxH);
        pdf.setLineWidth(0.2);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(9);
        pdf.setTextColor(31, 41, 55);
        const subTitle = pdf.splitTextToSize(block.text.toUpperCase(), contentWidth - 6)[0] as string;
        const titleW = pdf.getTextWidth(subTitle);
        if (highlightQuotes.some((q) => textContainsQuote(block.text, q) || textContainsQuote(subTitle, q))) {
          pdf.setFillColor(...HL_FILL);
          pdf.rect(MARGIN + 2.5, boxTop + 1.5, Math.min(titleW + 1, contentWidth - 5), 5, 'F');
        }
        pdf.text(subTitle, MARGIN + 3, boxTop + 5.4);
        y = boxTop + boxH + 3;
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
      } else if (block.type === 'paragraph') {
        y = writeWrappedHighlighted(pdf, block.text, MARGIN, y, contentWidth, 5, highlightQuotes);
        y += 3;
      } else if (block.type === 'list') {
        for (let li = 0; li < block.items.length; li += 1) {
          const bullet = block.ordered ? `${li + 1}. ` : '• ';
          const line = `${bullet}${block.items[li]}`;
          y = writeWrappedHighlighted(pdf, line, MARGIN, y, contentWidth, 5, highlightQuotes);
        }
        y += 3;
      } else if (block.type === 'table') {
        y = ensureSpace(pdf, y, 20);
        autoTable(pdf, {
          startY: y,
          head: [block.headers],
          body: block.rows,
          margin: { left: MARGIN, right: MARGIN },
          styles: { fontSize: 9, cellPadding: 2, overflow: 'linebreak' },
          headStyles: { fillColor: NAVY, textColor: 255, fontStyle: 'bold' },
        });
        y = ((pdf as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? y) + 4;
      } else if (block.type === 'image') {
        const frame = imageData.get(block.frameId);
        if (!frame) {
          pdf.setFontSize(9);
          pdf.setTextColor(156, 163, 175);
          y = writeWrapped(
            pdf,
            block.caption ? `[Screenshot unavailable: ${block.caption}]` : '[Screenshot unavailable]',
            MARGIN,
            y,
            contentWidth,
            4.5,
          );
          pdf.setTextColor(31, 41, 55);
          pdf.setFontSize(10);
          y += 2;
          continue;
        }
        const maxH = 70;
        let w = contentWidth;
        let h = (frame.height * w) / frame.width;
        if (h > maxH) {
          h = maxH;
          w = (frame.width * h) / frame.height;
        }
        y = ensureSpace(pdf, y, h + 8);
        const imgX = MARGIN;
        const imgY = y;
        pdf.addImage(frame.dataUrl, 'JPEG', imgX, imgY, w, h);
        drawImageMarksOnPdf(
          pdf,
          sectionImageMarks.filter((m) => m.frame_id === block.frameId),
          imgX,
          imgY,
          w,
          h,
        );
        y += h + 2;
        if (block.caption) {
          pdf.setFontSize(8);
          pdf.setTextColor(107, 114, 128);
          y = writeWrapped(pdf, block.caption, MARGIN, y, contentWidth, 4);
          pdf.setTextColor(31, 41, 55);
          pdf.setFontSize(10);
        }
        y += 3;
      }
    }

    y = writeSectionCommentsList(pdf, y, contentWidth, sectionComments);
    y += 4;
  }

  pdf.save(safeFilename(sop.title));
}
